import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ACTION_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/datetime";

const ACTION_COLORS = {
  login_success: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/20",
  login_failed: "bg-destructive/10 text-destructive border-destructive/20",
  login_blocked: "bg-stone-100 text-stone-700 border-stone-200",
  logout: "bg-stone-100 text-stone-600 border-stone-200",
  user_created: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  user_updated: "bg-blue-50 text-blue-700 border-blue-200",
  user_deleted: "bg-destructive/10 text-destructive border-destructive/20",
  password_reset: "bg-purple-50 text-purple-700 border-purple-200",
  company_created: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  company_updated: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/20",
  membership_created: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  membership_updated: "bg-blue-50 text-blue-700 border-blue-200",
  membership_deleted: "bg-destructive/10 text-destructive border-destructive/20",
};

function formatDate(iso) {
  return formatDateTime(iso);
}

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [userQuery, setUserQuery] = useState("all");
  const [companyQuery, setCompanyQuery] = useState("all");
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== "all") params.action = action;
      if (userQuery !== "all") params.user_id = userQuery;
      if (companyQuery !== "all") params.company_id = companyQuery;
      const { data } = await api.get("/audit/logs", { params });
      setLogs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar auditoría");
    } finally {
      setLoading(false);
    }
  };

  const loadDicts = async () => {
    try {
      const [u, c] = await Promise.all([
        api.get("/team/users"),
        api.get("/companies/"),
      ]);
      setUsers(u.data);
      setCompanies(c.data);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => { loadDicts(); }, []);
  useEffect(() => { load(); }, [action, userQuery, companyQuery]);

  const actionOptions = useMemo(
    () => [
      { value: "all", label: "Todos los eventos" },
      ...Object.entries(ACTION_LABELS).map(([v, l]) => ({ value: v, label: l })),
    ],
    []
  );

  const companyMap = useMemo(() => {
    const map = {};
    for (const c of companies) map[c.id] = c;
    return map;
  }, [companies]);

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="audit-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 text-brand-gold" />
            Auditoría
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            Registro cronológico de accesos, cambios de usuarios y acciones sensibles — con contexto multi-empresa.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-audit-button">
          <RefreshCw className="h-4 w-4 mr-2" /> Recargar
        </Button>
      </div>

      <div className="card-elevated p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Tipo de evento</div>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                {actionOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`filter-action-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Usuario</div>
            <Select value={userQuery} onValueChange={setUserQuery}>
              <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Empresa</div>
            <Select value={companyQuery} onValueChange={setCompanyQuery}>
              <SelectTrigger data-testid="filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                <SelectItem value="none" data-testid="filter-company-none">— Sin empresa (sistema) —</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`filter-company-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60">
              <TableHead>Fecha</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Detalle</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-16">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
              </TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-16 text-stone-500">
                Sin eventos registrados para los filtros seleccionados.
              </TableCell></TableRow>
            ) : (
              logs.map((l) => {
                const c = l.company_id ? companyMap[l.company_id] : null;
                return (
                  <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                    <TableCell className="whitespace-nowrap text-sm text-stone-600">{formatDate(l.timestamp)}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-brand-ink">{l.user_name || "—"}</div>
                    </TableCell>
                    <TableCell>
                      {l.company_id ? (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span
                            className="h-4 w-4 rounded flex items-center justify-center text-white text-[9px] font-bold"
                            style={{ backgroundColor: (c?.color) || "#00A36C" }}
                          >
                            {(c?.name || l.company_name || "?")[0]}
                          </span>
                          <span className="text-stone-700">{c?.name || l.company_name || "—"}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-stone-400 italic">sistema</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`font-semibold border ${ACTION_COLORS[l.action] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                        {ACTION_LABELS[l.action] || l.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-stone-600 max-w-lg">{l.detail}</TableCell>
                    <TableCell className="text-xs text-stone-500 font-mono">{l.ip}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}
